import { DateTimePicker } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Point from "ol/geom/Point";
import { Icon, Style } from "ol/style";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { PointOfInterestType } from "~/api/GeneratedApi";
import { useVectorLayer } from "~/pages/MapPage/functions/useVectorLayer";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import { useGenericDraw } from "~/utils/hooks/useGenericDraw";
import { poiTypes, usePoiItems } from "../hooks/usePoiItems";
import { PoiItemScreenEnum } from "./PoiItemsContainer";
import { PoiItemTableProps } from "./PoiItemTable";

export const PoiItemAddContent = ({
  id,
  map,
  setActiveScreen,
}: PoiItemTableProps) => {
  const { t } = useTranslation();
  const [description, setDescription] = React.useState<string>("");
  const [date, setDate] = React.useState<Date>(new Date());
  const apiErrorHandler = useApiErrorHandler();
  const { selectedPoiItem, setSelectedPoiItem, poiIcon, addPoiItem } =
    usePoiItems({ id });
  const layerOptions = React.useMemo(
    () => ({
      style: new Style({
        image: new Icon({
          src: `${window.location.origin}${poiIcon}`,
          scale: 1.5,
        }),
      }),
      zIndex: 1000,
    }),
    [poiIcon],
  );
  const autoCompleteRef = useRef<HTMLElement>(null);
  const { vectorLayer: drawLayer } = useVectorLayer({
    map,
    layerOptions,
  });
  const { itemCoordinates, setItemCoordinates } = useGenericDraw({
    map,
    enabled: true,
    drawLayer,
    drawIcon: poiIcon,
    GeometryClass: Point,
  });

  const [x, y] = itemCoordinates;
  const clearSettings = React.useCallback(() => {
    setDescription("");
    setItemCoordinates([]);
    drawLayer.getSource().clear();
    autoCompleteRef.current?.focus();
  }, [drawLayer, setItemCoordinates]);

  return (
    <Box
      sx={{
        width: "100%",
        margin: "auto",
        display: "grid",

        gridTemplateColumns: "1fr minmax(60%,auto) 1fr",
      }}
    >
      <Box />
      <Box sx={{ paddingBottom: "10px", display: "grid", gap: "15px" }}>
        <Box>
          <Typography variant="h6">{t("projects:poiDesc")}</Typography>
          <Autocomplete
            getOptionLabel={(opt) => t(`poi-item-enum:${opt}`)}
            renderInput={(params) => (
              <Paper
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <TextField
                  {...params}
                  inputRef={autoCompleteRef}
                  variant="outlined"
                />
              </Paper>
            )}
            onChange={(_e, v) => {
              if (v) setSelectedPoiItem(v);
            }}
            value={selectedPoiItem}
            options={poiTypes}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "5px",
          }}
        >
          <TextField
            key="longitude"
            label="Euref X"
            inputProps={{ type: "number" }}
            onChange={(e) => {
              setItemCoordinates((prev) => [Number(e.target.value), prev[1]]);
            }}
            value={itemCoordinates?.[0] ?? ""}
          />
          <TextField
            key="latitude"
            inputProps={{ type: "number" }}
            onChange={(e) => {
              setItemCoordinates((prev) => [prev[0], Number(e.target.value)]);
            }}
            label="Euref Y"
            value={itemCoordinates[1] ?? ""}
          />
        </Box>
        <DateTimePicker
          inputFormat="dd/MM/yyyy HH:mm"
          mask="__/__/____ __:__"
          label={t("projects:timestamp")}
          value={date}
          onChange={(d) => {
            if (!d) return;
            setDate(d);
          }}
          renderInput={(params) => <TextField {...params} />}
        />
        <TextField
          multiline
          rows={4}
          label={t("projects:description")}
          variant="outlined"
          key="desc"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <Button
          color="warning"
          onClick={() => {
            setActiveScreen(PoiItemScreenEnum.PoiList);
          }}
        >
          {t("general:cancel")}
        </Button>
        <Button
          color="success"
          onClick={() => {
            addPoiItem({
              poiItem: {
                description,

                type: PointOfInterestType[selectedPoiItem],
                coordinates: { x, y },
                date,
              },
              projectId: id,
            })
              .then(clearSettings)
              .catch(apiErrorHandler);
          }}
        >
          {t("general:save")}
        </Button>
      </Box>
    </Box>
  );
};
